import { Autocomplete, Box, TextField, Typography } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuditLogs } from '@src/tools/key-vault/api/audit-api'
import { useSubscriptions, useKeyVaultsList } from '@src/tools/key-vault/api/keyvaults-api'
import { AuditLogEntry, AuditLogFilters, Subscription } from '@src/tools/key-vault/types/KeyVault'
import { format } from 'date-fns'

const AuditPage = () => {
  const { t, i18n } = useTranslation()
  const [subscriptions, isLoadingSubscriptions] = useSubscriptions()
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null)
  const [keyVaults] = useKeyVaultsList(selectedSubscription?.id ?? null)

  const [filters, setFilters] = useState<AuditLogFilters>({
    offset: 0,
    limit: 50,
  })

  const [auditLogs, isFetching] = useAuditLogs(filters)

  const handleSubscriptionChange = (_e: unknown, value: Subscription | null) => {
    setSelectedSubscription(value)
    setFilters((prev) => ({ ...prev, keyVaultName: undefined, offset: 0 }))
  }

  const columns: GridColDef[] = [
    {
      field: 'createdAt',
      headerName: t('keyVault.audit.date'),
      flex: 1,
      valueFormatter: (value: string) => {
        if (!value) return ''
        return format(new Date(value), 'yyyy-MM-dd HH:mm:ss')
      },
    },
    {
      field: 'createdBy',
      headerName: t('keyVault.audit.user'),
      flex: 1,
    },
    {
      field: 'keyVaultName',
      headerName: t('keyVault.keyVaultName'),
      flex: 1,
    },
    {
      field: 'secretName',
      headerName: t('keyVault.secretKey'),
      flex: 1,
    },
  ]

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('keyVault.audit.title')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Autocomplete
          options={subscriptions}
          getOptionLabel={(option) => option.name}
          loading={isLoadingSubscriptions}
          value={selectedSubscription}
          onChange={handleSubscriptionChange}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => <TextField {...params} label={t('keyVault.subscription')} size="small" />}
          sx={{ minWidth: 250 }}
        />
        <Autocomplete
          options={keyVaults}
          value={filters.keyVaultName ?? null}
          onChange={(_e, value) => setFilters((prev) => ({ ...prev, keyVaultName: value ?? undefined, offset: 0 }))}
          disabled={!selectedSubscription}
          renderInput={(params) => <TextField {...params} label={t('keyVault.keyVaultName')} size="small" />}
          sx={{ minWidth: 250 }}
        />
        <TextField
          label={t('keyVault.audit.user')}
          size="small"
          value={filters.user ?? ''}
          onChange={(e) => setFilters((prev) => ({ ...prev, user: e.target.value || undefined, offset: 0 }))}
          sx={{ minWidth: 200 }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 400 }}>
        <DataGrid
          sx={{
            flex: 1,
            border: 'none',
            '.MuiDataGrid-columnHeaders div:first-of-type': {
              bgcolor: 'secondary.main',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '8px 8px 0 0',
            },
            '.MuiDataGrid-cell': {
              fontWeight: 500,
            },
            '.MuiDataGrid-sortIcon': { color: 'white' },
          }}
          rows={auditLogs}
          columns={columns}
          loading={isFetching}
          pageSizeOptions={[25, 50, 100]}
          paginationModel={{ page: filters.offset / filters.limit, pageSize: filters.limit }}
          onPaginationModelChange={(model) =>
            setFilters((prev) => ({
              ...prev,
              offset: model.page * model.pageSize,
              limit: model.pageSize,
            }))
          }
          paginationMode="server"
          rowCount={auditLogs.length < filters.limit ? filters.offset + auditLogs.length : -1}
          disableRowSelectionOnClick
          getRowId={(row: AuditLogEntry) => row.id}
          localeText={i18n.language === 'fr' ? { noRowsLabel: 'Aucun enregistrement' } : undefined}
        />
      </Box>
    </Box>
  )
}

export default AuditPage
